import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  MessageSquare,
  Pencil,
  Save,
  Shield,
  Trash2,
  Upload,
  UserCheck,
  Users,
  Video,
  X,
} from "lucide-react"

import {
  useOrganization,
  useOrganizationProfile,
  useUploadWelcomeVideo,
  useDeleteWelcomeVideo,
} from "../../hooks/useProfile"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Separator } from "@/modules/shared/ui/components/separator"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"

const organizationSchema = z.object({
  name: z
    .string()
    .min(1, "Company name is required")
    .max(255, "Company name is too long"),
  businessPhone: z
    .string()
    .max(50, "Phone number is too long")
    .optional()
    .or(z.literal("")),
  description: z
    .string()
    .max(2000, "Description is too long")
    .optional()
    .or(z.literal("")),
  welcomeMessage: z
    .string()
    .max(2000, "Welcome message is too long")
    .optional()
    .or(z.literal("")),
  address: z.object({
    line1: z.string().min(1, "Address line 1 is required"),
    line2: z.string().optional().or(z.literal("")),
    city: z.string().min(1, "City is required"),
    state: z.string().optional().or(z.literal("")),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
    complexNumber: z.string().optional().or(z.literal("")),
    complexName: z.string().optional().or(z.literal("")),
  }),
})

type OrganizationFormValues = z.infer<typeof organizationSchema>

const MAX_VIDEO_BYTES = 100 * 1024 * 1024
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/quicktime"]

export default function OrganizationProfilePage() {
  const navigate = useNavigate()
  const { OrganizationId } = useParams<{ OrganizationId: string }>()
  const orgId = OrganizationId

  const {
    data: organization,
    isLoading: isLoadingOrg,
    error: orgError,
    refetch: refetchOrg,
  } = useOrganization(orgId)

  const { data: overview, isLoading: isLoadingOverview } =
    useOrganizationProfile(orgId)

  const { updateOrganization } = useOrganizationsService()
  const uploadVideo = useUploadWelcomeVideo(orgId ?? "")
  const deleteVideo = useDeleteWelcomeVideo(orgId ?? "")

  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: "",
      businessPhone: "",
      description: "",
      welcomeMessage: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
        complexNumber: "",
        complexName: "",
      },
    },
  })

  useEffect(() => {
    if (!organization) return
    form.reset({
      name: organization.name,
      businessPhone: organization.businessPhone ?? "",
      description: organization.description ?? "",
      welcomeMessage: organization.welcomeMessage ?? "",
      address: {
        line1: organization.physicalAddress?.line1 ?? "",
        line2: organization.physicalAddress?.line2 ?? "",
        city: organization.physicalAddress?.city ?? "",
        state: organization.physicalAddress?.state ?? "",
        postalCode: organization.physicalAddress?.postalCode ?? "",
        country: organization.physicalAddress?.country ?? "",
        complexNumber: organization.physicalAddress?.complexNumber ?? "",
        complexName: organization.physicalAddress?.complexName ?? "",
      },
    })
  }, [organization, form])

  const handleCancel = () => {
    if (organization) {
      form.reset({
        name: organization.name,
        businessPhone: organization.businessPhone ?? "",
        description: organization.description ?? "",
        welcomeMessage: organization.welcomeMessage ?? "",
        address: {
          line1: organization.physicalAddress?.line1 ?? "",
          line2: organization.physicalAddress?.line2 ?? "",
          city: organization.physicalAddress?.city ?? "",
          state: organization.physicalAddress?.state ?? "",
          postalCode: organization.physicalAddress?.postalCode ?? "",
          country: organization.physicalAddress?.country ?? "",
          complexNumber: organization.physicalAddress?.complexNumber ?? "",
          complexName: organization.physicalAddress?.complexName ?? "",
        },
      })
    }
    setIsEditing(false)
  }

  const onSubmit = async (values: OrganizationFormValues) => {
    if (!orgId) return
    try {
      setIsSaving(true)
      await updateOrganization(orgId, {
        name: values.name.trim(),
        businessPhone: values.businessPhone?.trim() || undefined,
        description: values.description?.trim() || undefined,
        welcomeMessage: values.welcomeMessage?.trim() || undefined,
        physicalAddress: {
          line1: values.address.line1.trim(),
          line2: values.address.line2?.trim() || undefined,
          city: values.address.city.trim(),
          state: values.address.state?.trim() || undefined,
          postalCode: values.address.postalCode.trim(),
          country: values.address.country.trim(),
          complexNumber: values.address.complexNumber?.trim() || undefined,
          complexName: values.address.complexName?.trim() || undefined,
        },
      })
      toast.success("Organisation updated successfully")
      await refetchOrg()
      setIsEditing(false)
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to update organisation"
      toast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      toast.error("Please choose an MP4 or MOV file")
      return
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error("Video must be 100MB or smaller")
      return
    }

    try {
      await uploadVideo.mutateAsync(file)
      toast.success("Welcome video uploaded")
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to upload welcome video"
      toast.error(msg)
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const handleVideoDelete = async () => {
    try {
      await deleteVideo.mutateAsync()
      toast.success("Welcome video removed")
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to remove welcome video"
      toast.error(msg)
    }
  }

  const formatDate = (value?: string | null) => {
    if (!value) return "—"
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  if (isLoadingOrg || isLoadingOverview) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner />
      </div>
    )
  }

  if (orgError || !organization) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-lg text-muted-foreground">
          Failed to load organisation
        </p>
        <Button onClick={() => refetchOrg()}>Retry</Button>
      </div>
    )
  }

  const welcomeVideoUrl = overview?.welcomeVideoBlobUrl ?? null
  const description = form.watch("description")
  const welcomeMessage = form.watch("welcomeMessage")

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/account/profile")}
          className="text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <Form {...form}>
        <form
          id="organization-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Organisation Details
                  </CardTitle>
                  {!isEditing ? (
                    <Button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      variant="outline"
                      size="sm"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={handleCancel}
                        variant="outline"
                        size="sm"
                        disabled={isSaving}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        form="organization-form"
                        size="sm"
                        disabled={isSaving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Company name"
                            {...field}
                            disabled={!isEditing}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormItem>
                    <FormLabel>Service</FormLabel>
                    <FormControl>
                      <Input value="Tempered Talent" disabled readOnly />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                  <FormField
                    control={form.control}
                    name="businessPhone"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Business Phone</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 0123456789"
                            {...field}
                            disabled={!isEditing}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />
                <Separator />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Address Information
                  </h3>

                  <FormField
                    control={form.control}
                    name="address.line1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 1</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Street address"
                            {...field}
                            disabled={!isEditing}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.line2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 2 (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Apartment, suite, etc."
                            {...field}
                            disabled={!isEditing}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="address.city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isEditing} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="address.state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State/Province</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isEditing} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="address.postalCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postal Code</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              disabled={!isEditing}
                              inputMode="numeric"
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value.replace(/\D/g, ""),
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address.complexNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Complex / Unit Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. Unit 4B"
                              {...field}
                              disabled={!isEditing}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="address.complexName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Complex / Estate Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. Sandton Office Park"
                              {...field}
                              disabled={!isEditing}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="address.country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!isEditing} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Organisation Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us about your organisation..."
                            rows={4}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <p
                    className={
                      description
                        ? "text-sm whitespace-pre-wrap"
                        : "text-sm italic text-muted-foreground"
                    }
                  >
                    {description || "No description added yet"}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Organisation Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatTile
                    icon={<Briefcase className="w-5 h-5 text-blue-600" />}
                    label="Open Vacancies"
                    value={overview?.vacancyCount ?? 0}
                    valueClassName="text-blue-600"
                  />
                  <StatTile
                    icon={<UserCheck className="w-5 h-5 text-green-600" />}
                    label="Candidates"
                    value={overview?.candidateCount ?? 0}
                    valueClassName="text-green-600"
                  />
                  <StatTile
                    icon={<Users className="w-5 h-5 text-purple-600" />}
                    label="Employees"
                    value={overview?.employeeCount ?? 0}
                    valueClassName="text-purple-600"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Welcome Video
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between border border-dashed rounded-md p-4">
                  <div>
                    <p className="font-medium text-sm">
                      {welcomeVideoUrl
                        ? "Replace welcome video"
                        : "Upload welcome video"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      MP4, MOV, max 100MB
                    </p>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime"
                      className="hidden"
                      onChange={handleVideoSelect}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadVideo.isPending}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadVideo.isPending ? "Uploading..." : "Choose Video"}
                    </Button>
                  </div>
                </div>

                {welcomeVideoUrl && (
                  <div className="flex flex-col items-center gap-3">
                    <video
                      src={welcomeVideoUrl}
                      controls
                      className="w-full max-h-96 rounded-md bg-black"
                    />
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        Current welcome video
                      </span>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleVideoDelete}
                        disabled={deleteVideo.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {deleteVideo.isPending ? "Removing..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Welcome Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  This message will be shown to new employees when they first
                  log in.
                </p>
                {isEditing ? (
                  <FormField
                    control={form.control}
                    name="welcomeMessage"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder="Write a welcome message for new employees..."
                            rows={4}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <p
                    className={
                      welcomeMessage
                        ? "text-sm whitespace-pre-wrap"
                        : "text-sm italic text-muted-foreground"
                    }
                  >
                    {welcomeMessage || "No welcome message added yet"}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl">Organisation Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow
                  icon={<Calendar className="w-5 h-5 text-muted-foreground" />}
                  label="Created"
                  value={formatDate(organization.createdAt)}
                />
                <Separator />
                <InfoRow
                  icon={<Briefcase className="w-5 h-5 text-muted-foreground" />}
                  label="Plan"
                  value={organization.plan}
                />
                <Separator />
                <InfoRow
                  icon={<Shield className="w-5 h-5 text-muted-foreground" />}
                  label="Status"
                  value={
                    <Badge
                      variant={
                        organization.status === "Active"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {organization.status}
                    </Badge>
                  }
                />
              </CardContent>
            </Card>
          </div>
        </form>
      </Form>
    </div>
  )
}

interface StatTileProps {
  icon: React.ReactNode
  label: string
  value: number
  valueClassName?: string
}

function StatTile({ icon, label, value, valueClassName }: StatTileProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 border rounded-md p-4">
      {icon}
      <span className={`text-3xl font-bold ${valueClassName ?? ""}`}>
        {value}
      </span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

interface InfoRowProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className="font-medium mt-0.5">{value}</div>
      </div>
    </div>
  )
}
